import React from "react";
import { useTranslation } from "react-i18next";
import { classNames, getTypeIcon, transformAmplifyError } from "../../Utils";

export const Alert = ({
  className = "",
  title = "",
  message = "",
  messages = [],
  errors = [],
  type = "warning",
  renderButton = () => {},
  renderActions = () => {},
}) => {
  const { t } = useTranslation();

  function renderTitle() {
    if (!title) return null;
    switch (type) {
      case "warning":
        return (
          <h3 className="text-sm font-medium text-yellow-800">{t(title)}</h3>
        );
      case "danger":
      case "error":
        return <h3 className="text-sm font-medium text-red-800">{t(title)}</h3>;
      case "info":
        return (
          <h3 className="text-sm font-medium text-blue-800">{t(title)}</h3>
        );
      case "success":
        return (
          <h3 className="text-sm font-medium text-green-800">{t(title)}</h3>
        );
      default:
        return (
          <h3 className="text-sm font-medium text-yellow-800">{t(title)}</h3>
        );
    }
  }

  function renderMessage(text) {
    switch (type) {
      case "warning":
        return <p className="text-sm text-yellow-700">{t(text)}</p>;
      case "danger":
      case "error":
        return <p className="text-sm text-red-700">{t(text)}</p>;
      case "info":
        return <p className="text-sm text-blue-700">{t(text)}</p>;
      case "success":
        return <p className="text-sm text-green-700">{t(text)}</p>;
      default:
        return <p className="text-sm text-yellow-700">{t(text)}</p>;
    }
  }

  function renderList(items) {
    if (items.length === 1) {
      return renderMessage(items[0]);
    }

    switch (type) {
      case "warning":
        return (
          <ul className="list-disc pl-5 space-y-1 text-sm text-yellow-700">
            {items.map((item, index) => (
              <li key={`${index}`}>{t(item)}</li>
            ))}
          </ul>
        );
      case "danger":
      case "error":
        return (
          <ul className="list-disc pl-5 space-y-1 text-sm text-red-700">
            {items.map((item, index) => (
              <li key={`${index}`}>{t(item)}</li>
            ))}
          </ul>
        );
      case "info":
        return (
          <ul className="list-disc pl-5 space-y-1 text-sm text-blue-700">
            {items.map((item, index) => (
              <li key={`${index}`}>{t(item)}</li>
            ))}
          </ul>
        );
      case "success":
        return (
          <ul className="list-disc pl-5 space-y-1 text-sm text-green-700">
            {items.map((item, index) => (
              <li key={`${index}`}>{t(item)}</li>
            ))}
          </ul>
        );
      default:
        return (
          <ul className="list-disc pl-5 space-y-1 text-sm text-yellow-700">
            {items.map((item, index) => (
              <li key={`${index}`}>{t(item)}</li>
            ))}
          </ul>
        );
    }
  }

  function renderIcon() {
    const Icon = getTypeIcon(type);
    switch (type) {
      case "warning":
        return <Icon className="w-5 h-5 text-yellow-400" aria-hidden="true" />;
      case "danger":
      case "error":
        return <Icon className="w-5 h-5 text-red-400" aria-hidden="true" />;
      case "info":
        return <Icon className="w-5 h-5 text-blue-400" aria-hidden="true" />;
      case "success":
        return <Icon className="w-5 h-5 text-green-400" aria-hidden="true" />;
      default:
        return <Icon className="w-5 h-5 text-yellow-400" aria-hidden="true" />;
    }
  }

  function renderMessages() {
    // Handle both Amplify errors (objects with .message) and regular string arrays
    let errorMessages = [];
    if (errors.length > 0) {
      // Check if errors are Amplify error objects or regular strings
      if (errors[0] && typeof errors[0] === 'object' && errors[0].message) {
        // Amplify errors - use transformAmplifyError
        errorMessages = transformAmplifyError(errors);
      } else {
        // Regular string array - use directly
        errorMessages = errors.filter(error => typeof error === 'string' && error.trim() !== '');
      }
    }
    
    if (errorMessages.length > 0) {
      return renderList(errorMessages);
    } else if (messages.length > 0) {
      return renderList(messages);
    } else if (message) {
      return renderMessage(message);
    } else if (!title) {
      return renderMessage("Error. Please try again later.");
    }
    return null;
  }

  function renderContent() {
    return (
      <div className="flex">
        <div className="flex-shrink-0">{renderIcon()}</div>
        <div className="ml-3 w-full">
          <div className="flex">
            <div>
              {renderTitle()}
              {renderMessages()}
            </div>
            {renderButton()}
          </div>
          {renderActions()}
        </div>
      </div>
    );
  }

  switch (type) {
    case "warning":
      return (
        <div
          className={classNames(className, "p-4 rounded-lg bg-yellow-50 border border-yellow-200")}
          role="alert"
        >
          {renderContent()}
        </div>
      );
    case "danger":
    case "error":
      return (
        <div
          className={classNames(className, "p-4 rounded-lg bg-red-50 border border-red-200")}
          role="alert"
        >
          {renderContent()}
        </div>
      );
    case "info":
      return (
        <div
          className={classNames(className, "p-4 rounded-lg bg-blue-50 border border-blue-200")}
          role="alert"
        >
          {renderContent()}
        </div>
      );
    case "success":
      return (
        <div
          className={classNames(className, "p-4 rounded-lg bg-green-50 border border-green-200")}
          role="alert"
        >
          {renderContent()}
        </div>
      );
    default:
      return (
        <div
          className={classNames(className, "p-4 rounded-lg bg-yellow-50 border border-yellow-200")}
          role="alert"
        >
          {renderContent()}
        </div>
      );
  }
};
